import { forwardRef, useImperativeHandle, useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { containerTypeZ, ContainerTypeZ } from '@libs/schemas';
import { MoneyFromDollars, MoneyFromCents } from '@libs/services/money';
import { ContainerImage } from '@libs/models';
import { ImageManager } from 'src/components/image-management';

interface ContainerTypeFormProps {
  initialValues?: Partial<ContainerTypeZ>;
  onSubmit: (
    values: ContainerTypeZ & { images?: ContainerImage[]; uploadingFiles?: File[] },
  ) => void;
  isEditing?: boolean;
  onUploadComplete?: () => void;
  disabled?: boolean;
  containerTypeId?: string;
}

export interface ContainerTypeFormRef {
  submitForm: () => void;
  clearUploadingFiles: () => void;
}

export const ContainerTypeForm = forwardRef<ContainerTypeFormRef, ContainerTypeFormProps>(
  (
    {
      initialValues,
      onSubmit,
      isEditing = false,
      disabled = false,
    },
    ref,
  ) => {
    const [images, setImages] = useState<ContainerImage[]>(
      (initialValues?.images as ContainerImage[]) || [],
    );
    const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
    // Track if images have been modified locally to prevent overwriting on initialValues change
    const imagesModifiedRef = useRef(false);
    const initialImagesRef = useRef<ContainerImage[]>(
      (initialValues?.images as ContainerImage[]) || [],
    );

    // Sync images with initialValues only when the form opens with new data
    // but don't overwrite if user has made changes
    useEffect(() => {
      const newInitialImages = (initialValues?.images as ContainerImage[]) || [];
      const currentInitialImages = initialImagesRef.current;
      
      // Compare by URL to see if initial values changed
      const currentUrls = new Set(currentInitialImages.map(img => img.url));
      const newUrls = new Set(newInitialImages.map(img => img.url));
      const urlsChanged = 
        currentUrls.size !== newUrls.size ||
        [...currentUrls].some(url => !newUrls.has(url)) ||
        [...newUrls].some(url => !currentUrls.has(url));
      
      // Only sync if initial values changed AND images haven't been modified locally
      if (urlsChanged && !imagesModifiedRef.current) {
        setImages(newInitialImages);
        initialImagesRef.current = newInitialImages;
      } else if (urlsChanged) {
        // Update the ref to track new baseline, but don't overwrite user changes
        initialImagesRef.current = newInitialImages;
      }
    }, [initialValues?.images]);
    const form = useForm<ContainerTypeZ>({
      resolver: zodResolver(containerTypeZ),
      mode: 'onSubmit',
      defaultValues: {
        ...initialValues,
        pricePerDay: initialValues?.pricePerDay
          ? MoneyFromCents(initialValues.pricePerDay).toUnit()
          : 0,
        preAuthAmount: initialValues?.preAuthAmount
          ? MoneyFromCents(initialValues.preAuthAmount).toUnit()
          : 0,
      },
    });

    const handleSubmit = (values: ContainerTypeZ) => {
      // Reset the modified flag on submit
      imagesModifiedRef.current = false;
      initialImagesRef.current = images;
      
      onSubmit({
        ...values,
        pricePerDay: MoneyFromDollars(values.pricePerDay).getAmount(),
        preAuthAmount: MoneyFromDollars(values.preAuthAmount).getAmount(),
        images,
        uploadingFiles,
      });
    };

    const handleImageRemove = async (imageUrl: string) => {
      // Just remove from local state - will be deleted from backend when form is saved
      const imageToRemove = images.find((img) => img.url === imageUrl);
      if (!imageToRemove) return;

      imagesModifiedRef.current = true;
      const updatedImages = images.filter((img) => img.url !== imageUrl);
      
      // If we removed the main image, set the first remaining image as main
      if (imageToRemove.isMainImage && updatedImages.length > 0) {
        updatedImages[0].isMainImage = true;
      }
      
      // Update display orders
      const finalImages = updatedImages.map((img, index) => ({
        ...img,
        displayOrder: img.isMainImage ? 0 : index,
      }));
      
      setImages(finalImages);
    };

    const handleFilesSelected = async (files: File[]) => {
      // Just add files to the queue - they'll be uploaded when the form is saved
      imagesModifiedRef.current = true;
      setUploadingFiles([...uploadingFiles, ...files]);
    };

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        form.handleSubmit((validData) => {
          handleSubmit(validData);
        })();
      },
      clearUploadingFiles: () => {
        setUploadingFiles([]);
      },
    }));

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <div className="grid grid-cols-2 gap-x-4 gap-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-text-light">Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter name" {...field} disabled={disabled} />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="size"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-text-light">Size</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={disabled}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="10yard">10 Yard</SelectItem>
                      <SelectItem value="20yard">20 Yard</SelectItem>
                      <SelectItem value="30yard">30 Yard</SelectItem>
                      <SelectItem value="40yard">40 Yard</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="maxWeight"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-text-light">Max Weight (lbs)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Enter max weight"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                      disabled={disabled}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="volume"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-text-light">Volume</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter volume" {...field} disabled={disabled} />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="pricePerDay"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-text-light">Price Per Day ($)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Enter price per day"
                      {...field}
                      onChange={(e) => field.onChange(parseFloat(e.target.value))}
                      disabled={disabled}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="preAuthAmount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-text-light">Pre-Auth Amount ($)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Enter pre-auth amount"
                      {...field}
                      onChange={(e) => field.onChange(parseFloat(e.target.value))}
                      disabled={disabled}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="capacityDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-text-light">Capacity Description</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter capacity description"
                      {...field}
                      disabled={disabled}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="dimensions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-text-light">Dimensions</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter dimensions" {...field} disabled={disabled} />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="recommendedUse"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-text-light">Recommended Use</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter recommended use" {...field} disabled={disabled} />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="imageUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-text-light">Legacy Image URL</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter image URL (deprecated)"
                      {...field}
                      disabled={disabled}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            {isEditing && (
              <FormField
                control={form.control}
                name="archived"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={disabled}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-text-light">Archived</FormLabel>
                    </div>
                  </FormItem>
                )}
              />
            )}
          </div>

          {/* Image Management Section */}
          <div className="mt-8">
            <ImageManager
              images={images}
              onImagesChange={(newImages) => {
                imagesModifiedRef.current = true;
                setImages(newImages);
              }}
              onImageRemove={handleImageRemove}
              onFilesSelected={handleFilesSelected}
              onUploadingFilesChange={setUploadingFiles}
              uploadingFiles={uploadingFiles}
              maxImages={10}
              className="w-full"
              disabled={disabled}
            />
          </div>
        </form>
      </Form>
    );
  },
);

ContainerTypeForm.displayName = 'ContainerTypeForm';
